/**
 * Scheduling Management Tab - Admin Panel
 * Manages shift schedules, templates, patterns, and shift swaps
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Calendar, 
  Clock,
  Plus,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Settings,
  Users,
  Repeat,
  ArrowLeftRight,
  Edit,
  Trash2,
  Copy,
  Check,
  X,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Day names
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const SHORT_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function SchedulingTab() {
  const [weekSchedule, setWeekSchedule] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [swapRequests, setSwapRequests] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('calendar'); // calendar, templates, patterns, swaps, generate
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showPatternModal, setShowPatternModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const fetchWeekSchedule = useCallback(async (date) => {
    try {
      const response = await api.get(`/workforce/scheduling/week/${date}`);
      setWeekSchedule(response.data);
    } catch (error) {
      console.error('Error fetching week schedule:', error);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await api.get('/workforce/scheduling/templates');
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  }, []);

  const fetchPatterns = useCallback(async () => {
    try {
      const response = await api.get('/workforce/scheduling/patterns');
      setPatterns(response.data.patterns || []);
    } catch (error) {
      console.error('Error fetching patterns:', error);
    }
  }, []);

  const fetchSwapRequests = useCallback(async () => {
    try {
      const response = await api.get('/workforce/scheduling/swap-requests');
      setSwapRequests(response.data.swap_requests || []);
    } catch (error) {
      console.error('Error fetching swap requests:', error);
    }
  }, []);

  const fetchStaff = useCallback(async () => {
    try {
      // Use dedicated scheduling staff list endpoint
      const response = await api.get('/workforce/scheduling/staff-list');
      setStaff(response.data.staff || []);
    } catch (error) {
      console.error('Error fetching staff:', error);
      // Fallback to admin users
      try {
        const fallbackResponse = await api.get('/admin/users');
        setStaff(fallbackResponse.data.filter(u => u.is_active));
      } catch (fallbackError) {
        console.error('Fallback fetch also failed:', fallbackError);
      }
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchWeekSchedule(selectedDate),
      fetchTemplates(),
      fetchPatterns(),
      fetchSwapRequests(),
      fetchStaff()
    ]);
    setLoading(false);
  }, [fetchWeekSchedule, fetchTemplates, fetchPatterns, fetchSwapRequests, fetchStaff, selectedDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    fetchWeekSchedule(selectedDate);
  }, [selectedDate, fetchWeekSchedule]);

  // Navigation
  const navigateWeek = (direction) => {
    const current = new Date(selectedDate);
    current.setDate(current.getDate() + (direction * 7));
    setSelectedDate(current.toISOString().split('T')[0]);
  };

  const goToToday = () => {
    setSelectedDate(new Date().toISOString().split('T')[0]);
  };

  // Template CRUD
  const handleSaveTemplate = async (template) => {
    try {
      if (template.id) {
        await api.put(`/workforce/scheduling/templates/${template.id}`, template);
      } else {
        await api.post('/workforce/scheduling/templates', template);
      }
      toast.success(template.id ? 'Template updated' : 'Template created');
      fetchTemplates();
      setShowTemplateModal(false);
      setEditingTemplate(null);
    } catch (error) {
      toast.error('Error saving template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Delete this template?')) return;
    try {
      await api.delete(`/workforce/scheduling/templates/${templateId}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Error deleting template');
    }
  };

  // Schedule CRUD
  const handleAddSchedule = async (scheduleData) => {
    try {
      await api.post('/workforce/scheduling/manual', scheduleData);
      toast.success('Schedule added');
      fetchWeekSchedule(selectedDate);
      setShowAddModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error adding schedule');
    }
  };

  const handleCancelSchedule = async (scheduleId, reason = 'Cancelled by manager') => {
    if (!confirm('Cancel this shift?')) return;
    try {
      await api.post(`/workforce/scheduling/${scheduleId}/cancel?reason=${encodeURIComponent(reason)}`);
      toast.success('Shift cancelled');
      fetchWeekSchedule(selectedDate);
    } catch (error) {
      toast.error('Error cancelling shift');
    }
  };

  // Swap approvals
  const handleSwapAction = async (requestId, action) => {
    try {
      await api.post(`/workforce/scheduling/swap-requests/${requestId}/${action}`);
      toast.success(`Swap request ${action}d`);
      fetchSwapRequests();
      fetchWeekSchedule(selectedDate);
    } catch (error) {
      toast.error(`Error ${action}ing swap request`);
    }
  };

  // Generate schedules
  const handleGenerateSchedules = async (staffIds, dateFrom, dateTo) => {
    try {
      const response = await api.post('/workforce/scheduling/generate', {
        staff_ids: staffIds,
        date_from: dateFrom,
        date_to: dateTo,
        use_auto_scheduling: true
      });
      toast.success(`Generated ${response.data.created_count} schedules`);
      fetchWeekSchedule(selectedDate);
    } catch (error) {
      toast.error('Error generating schedules');
    }
  };

  // Seed templates
  const handleSeedTemplates = async () => {
    try {
      await api.post('/workforce/scheduling/seed-templates');
      toast.success('Default templates created');
      fetchTemplates();
    } catch (error) {
      toast.error('Error seeding templates');
    }
  };

  const pendingSwaps = swapRequests.filter(r => r.status === 'pending');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading text-foreground">Scheduling</h2>
          <p className="text-muted-foreground">Manage shift schedules, templates, and swap requests</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={() => setShowAddModal(true)}
            className="gap-2"
            data-testid="add-schedule-btn"
          >
            <Plus className="w-4 h-4" />
            Add Shift
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-border overflow-x-auto pb-px">
        {[
          { id: 'calendar', label: 'Week View', icon: Calendar },
          { id: 'templates', label: 'Templates', icon: Clock },
          { id: 'patterns', label: 'Staff Patterns', icon: Repeat },
          { id: 'swaps', label: 'Swap Requests', icon: ArrowLeftRight, badge: pendingSwaps.length },
          { id: 'generate', label: 'Auto-Generate', icon: Settings }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap
              ${activeTab === tab.id 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            data-testid={`scheduling-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.badge > 0 && (
              <span className="px-1.5 py-0.5 text-xs bg-amber-500 text-white rounded-full">{tab.badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        {/* Calendar Tab */}
        {activeTab === 'calendar' && weekSchedule && (
          <div>
            {/* Week Navigation */}
            <div className="p-4 border-b border-border flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={() => navigateWeek(-1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="text-center">
                <h3 className="font-medium text-foreground">
                  {weekSchedule.week_start} - {weekSchedule.week_end}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {weekSchedule.total_shifts} shifts · {weekSchedule.total_hours} hours
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={goToToday}>Today</Button>
                <Button variant="ghost" size="sm" onClick={() => navigateWeek(1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Week Grid */}
            <div className="overflow-x-auto">
              <div className="min-w-[800px]">
                {/* Day Headers */}
                <div className="grid grid-cols-7 border-b border-border">
                  {Object.keys(weekSchedule.by_date).map((date, index) => {
                    const isToday = date === new Date().toISOString().split('T')[0];
                    const daySchedules = weekSchedule.by_date[date] || [];
                    return (
                      <div 
                        key={date} 
                        className={`p-3 text-center border-r border-border last:border-r-0 ${isToday ? 'bg-primary/5' : ''}`}
                      >
                        <p className={`text-xs font-medium ${isToday ? 'text-primary' : 'text-muted-foreground'}`}>
                          {SHORT_DAYS[index]}
                        </p>
                        <p className={`text-lg font-bold ${isToday ? 'text-primary' : 'text-foreground'}`}>
                          {new Date(date).getDate()}
                        </p>
                        <p className="text-xs text-muted-foreground">{daySchedules.length} shifts</p>
                      </div>
                    );
                  })}
                </div>

                {/* Schedule Cells */}
                <div className="grid grid-cols-7 min-h-[300px]">
                  {Object.entries(weekSchedule.by_date).map(([date, schedules]) => (
                    <div key={date} className="border-r border-border last:border-r-0 p-2 space-y-2">
                      {schedules.length === 0 ? (
                        <div className="text-center py-4 text-muted-foreground text-sm">No shifts</div>
                      ) : (
                        schedules.map(schedule => (
                          <div 
                            key={schedule.id}
                            className="p-2 rounded-lg text-xs cursor-pointer hover:opacity-90 transition-opacity"
                            style={{ backgroundColor: schedule.color + '20', borderLeft: `3px solid ${schedule.color}` }}
                            onClick={() => setEditingSchedule(schedule)}
                          >
                            <p className="font-medium text-foreground truncate">{schedule.staff_name}</p>
                            <p className="text-muted-foreground">{schedule.start_time} - {schedule.end_time}</p>
                            {schedule.is_leave && (
                              <span className="inline-block mt-1 px-1 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-[10px] uppercase">
                                {schedule.leave_type}
                              </span>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-medium text-foreground">Shift Templates</h3>
              <div className="flex gap-2">
                {templates.length === 0 && (
                  <Button variant="outline" size="sm" onClick={handleSeedTemplates}>
                    Seed Defaults
                  </Button>
                )}
                <Button size="sm" onClick={() => { setEditingTemplate(null); setShowTemplateModal(true); }}>
                  <Plus className="w-4 h-4 mr-1" /> New Template
                </Button>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {templates.map(template => (
                <div 
                  key={template.id} 
                  className="p-4 rounded-xl border border-border"
                  style={{ borderLeftColor: template.color, borderLeftWidth: '4px' }}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-foreground">{template.name}</h4>
                      {template.is_default && (
                        <span className="text-xs text-primary">Default</span>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setEditingTemplate(template); setShowTemplateModal(true); }}>
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => handleDeleteTemplate(template.id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-muted-foreground">
                    <p>{template.start_time} - {template.end_time}</p>
                    <p>{template.break_minutes} min break</p>
                  </div>
                </div>
              ))}
              {templates.length === 0 && (
                <div className="col-span-full p-8 text-center text-muted-foreground">
                  No templates yet. Click &quot;Seed Defaults&quot; or create your own.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Patterns Tab */}
        {activeTab === 'patterns' && (
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-medium text-foreground">Staff Schedule Patterns</h3>
              <Button size="sm" onClick={() => setShowPatternModal(true)}>
                <Plus className="w-4 h-4 mr-1" /> New Pattern
              </Button>
            </div>

            <div className="space-y-4">
              {patterns.map(pattern => (
                <div key={pattern.id} className="p-4 rounded-xl border border-border">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-foreground">{pattern.staff_name}</h4>
                      <p className="text-sm text-muted-foreground capitalize">{pattern.staff_role}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs ${pattern.is_active ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'}`}>
                      {pattern.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {DAYS.map((day, index) => (
                      <span 
                        key={day}
                        className={`px-2 py-1 rounded text-xs ${
                          pattern.days_off?.includes(index) 
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' 
                            : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                        }`}
                      >
                        {SHORT_DAYS[index]} {pattern.days_off?.includes(index) ? 'OFF' : ''}
                      </span>
                    ))}
                  </div>
                  {pattern.template && (
                    <p className="mt-2 text-sm text-muted-foreground">
                      Template: {pattern.template.name} ({pattern.template.start_time} - {pattern.template.end_time})
                    </p>
                  )}
                </div>
              ))}
              {patterns.length === 0 && (
                <div className="p-8 text-center text-muted-foreground">
                  No patterns defined. Create patterns to enable auto-scheduling.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Swap Requests Tab */}
        {activeTab === 'swaps' && (
          <div className="p-4">
            <h3 className="font-medium text-foreground mb-4">Shift Swap Requests</h3>
            
            {swapRequests.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                No swap requests
              </div>
            ) : (
              <div className="space-y-4">
                {swapRequests.map(request => (
                  <div 
                    key={request.id} 
                    className={`p-4 rounded-xl border ${
                      request.status === 'pending' ? 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/10' :
                      request.status === 'approved' ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/10' :
                      'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-foreground">{request.requesting_staff_name}</span>
                          <ArrowLeftRight className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium text-foreground">{request.target_staff_name}</span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {request.requesting_shift_date} ↔ {request.target_shift_date}
                        </p>
                        {request.reason && (
                          <p className="text-sm text-muted-foreground mt-1">Reason: {request.reason}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs capitalize
                          ${request.status === 'pending' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' : ''}
                          ${request.status === 'approved' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : ''}
                          ${request.status === 'rejected' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' : ''}`}
                        >
                          {request.status}
                        </span>
                        {request.status === 'pending' && (
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              className="h-7"
                              onClick={() => handleSwapAction(request.id, 'approve')}
                            >
                              <Check className="w-3 h-3 mr-1" /> Approve
                            </Button>
                            <Button 
                              variant="destructive" 
                              size="sm" 
                              className="h-7"
                              onClick={() => handleSwapAction(request.id, 'reject')}
                            >
                              <X className="w-3 h-3 mr-1" /> Reject
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Generate Tab */}
        {activeTab === 'generate' && (
          <GenerateSchedulesPanel 
            staff={staff}
            templates={templates}
            onGenerate={handleGenerateSchedules}
          />
        )}
      </div>

      {/* Add Schedule Modal */}
      {showAddModal && (
        <AddScheduleModal
          staff={staff}
          templates={templates}
          onClose={() => setShowAddModal(false)}
          onSave={handleAddSchedule}
        />
      )}

      {/* Template Modal */}
      {showTemplateModal && (
        <TemplateModal
          template={editingTemplate}
          onClose={() => { setShowTemplateModal(false); setEditingTemplate(null); }}
          onSave={handleSaveTemplate}
        />
      )}

      {/* Pattern Modal */}
      {showPatternModal && (
        <PatternModal
          staff={staff}
          templates={templates}
          onClose={() => setShowPatternModal(false)}
          onSave={async (pattern) => {
            try {
              await api.post('/workforce/scheduling/patterns', pattern);
              toast.success('Pattern created');
              fetchPatterns();
              setShowPatternModal(false);
            } catch (error) {
              toast.error('Error creating pattern');
            }
          }}
        />
      )}

      {/* Edit Schedule Modal */}
      {editingSchedule && (
        <EditScheduleModal
          schedule={editingSchedule}
          templates={templates}
          onClose={() => setEditingSchedule(null)}
          onCancel={() => handleCancelSchedule(editingSchedule.id)}
        />
      )}
    </div>
  );
}

// Add Schedule Modal
function AddScheduleModal({ staff, templates, onClose, onSave }) {
  const [form, setForm] = useState({
    staff_id: '',
    date: new Date().toISOString().split('T')[0],
    start_time: '06:45',
    end_time: '16:45',
    break_minutes: 30,
    shift_template_id: '',
    notes: ''
  });

  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setForm(prev => ({
        ...prev,
        shift_template_id: templateId,
        start_time: template.start_time,
        end_time: template.end_time,
        break_minutes: template.break_minutes
      }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-medium text-foreground mb-4">Add Shift</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Staff Member</label>
            <select
              value={form.staff_id}
              onChange={(e) => setForm(prev => ({ ...prev, staff_id: e.target.value }))}
              className="w-full p-2 rounded-lg border border-border bg-background text-foreground"
            >
              <option value="">Select staff...</option>
              {staff.map(s => (
                <option key={s.id} value={s.id}>{s.name} ({s.role})</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Date</label>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Template (Optional)</label>
            <select
              value={form.shift_template_id}
              onChange={(e) => handleTemplateSelect(e.target.value)}
              className="w-full p-2 rounded-lg border border-border bg-background text-foreground"
            >
              <option value="">Custom times...</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name} ({t.start_time} - {t.end_time})</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Start Time</label>
              <Input
                type="time"
                value={form.start_time}
                onChange={(e) => setForm(prev => ({ ...prev, start_time: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">End Time</label>
              <Input
                type="time"
                value={form.end_time}
                onChange={(e) => setForm(prev => ({ ...prev, end_time: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Break (Minutes)</label>
            <Input
              type="number"
              value={form.break_minutes}
              onChange={(e) => setForm(prev => ({ ...prev, break_minutes: parseInt(e.target.value) }))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Notes</label>
            <Input
              value={form.notes}
              onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Optional notes..."
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(form)} disabled={!form.staff_id || !form.date}>Add Shift</Button>
        </div>
      </div>
    </div>
  );
}

// Template Modal
function TemplateModal({ template, onClose, onSave }) {
  const [form, setForm] = useState(template || {
    name: '',
    start_time: '06:45',
    end_time: '16:45',
    break_minutes: 30,
    is_default: false,
    color: '#3b82f6'
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl max-w-md w-full p-6">
        <h3 className="text-lg font-medium text-foreground mb-4">
          {template ? 'Edit Template' : 'New Template'}
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Template Name</label>
            <Input
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Morning Shift"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Start Time</label>
              <Input
                type="time"
                value={form.start_time}
                onChange={(e) => setForm(prev => ({ ...prev, start_time: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">End Time</label>
              <Input
                type="time"
                value={form.end_time}
                onChange={(e) => setForm(prev => ({ ...prev, end_time: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Break (Minutes)</label>
            <Input
              type="number"
              value={form.break_minutes}
              onChange={(e) => setForm(prev => ({ ...prev, break_minutes: parseInt(e.target.value) }))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Color</label>
            <Input
              type="color"
              value={form.color}
              onChange={(e) => setForm(prev => ({ ...prev, color: e.target.value }))}
              className="h-10 p-1"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_default"
              checked={form.is_default}
              onChange={(e) => setForm(prev => ({ ...prev, is_default: e.target.checked }))}
              className="w-4 h-4 rounded border-border"
            />
            <label htmlFor="is_default" className="text-sm text-foreground">Set as default template</label>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(form)} disabled={!form.name}>Save Template</Button>
        </div>
      </div>
    </div>
  );
}

// Pattern Modal
function PatternModal({ staff, templates, onClose, onSave }) {
  const [form, setForm] = useState({
    staff_id: '',
    pattern_type: 'fixed_days_off',
    days_off: [6], // Sunday by default
    shift_template_id: '',
    effective_from: new Date().toISOString().split('T')[0]
  });

  const toggleDayOff = (dayIndex) => {
    setForm(prev => ({
      ...prev,
      days_off: prev.days_off.includes(dayIndex)
        ? prev.days_off.filter(d => d !== dayIndex)
        : [...prev.days_off, dayIndex]
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-medium text-foreground mb-4">New Schedule Pattern</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Staff Member</label>
            <select
              value={form.staff_id}
              onChange={(e) => setForm(prev => ({ ...prev, staff_id: e.target.value }))}
              className="w-full p-2 rounded-lg border border-border bg-background text-foreground"
            >
              <option value="">Select staff...</option>
              {staff.map(s => (
                <option key={s.id} value={s.id}>{s.name} ({s.role})</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Shift Template</label>
            <select
              value={form.shift_template_id}
              onChange={(e) => setForm(prev => ({ ...prev, shift_template_id: e.target.value }))}
              className="w-full p-2 rounded-lg border border-border bg-background text-foreground"
            >
              <option value="">Select template...</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name} ({t.start_time} - {t.end_time})</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Days Off</label>
            <div className="flex flex-wrap gap-2">
              {DAYS.map((day, index) => (
                <button
                  key={day}
                  type="button"
                  onClick={() => toggleDayOff(index)}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors
                    ${form.days_off.includes(index) 
                      ? 'bg-red-500 text-white' 
                      : 'bg-muted text-foreground hover:bg-muted/80'}`}
                >
                  {SHORT_DAYS[index]}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Click days to mark as days off</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Effective From</label>
            <Input
              type="date"
              value={form.effective_from}
              onChange={(e) => setForm(prev => ({ ...prev, effective_from: e.target.value }))}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(form)} disabled={!form.staff_id || !form.shift_template_id}>
            Create Pattern
          </Button>
        </div>
      </div>
    </div>
  );
}

// Edit Schedule Modal
function EditScheduleModal({ schedule, templates, onClose, onCancel }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl max-w-md w-full p-6">
        <h3 className="text-lg font-medium text-foreground mb-4">Shift Details</h3>
        
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Staff:</span>
            <span className="text-foreground font-medium">{schedule.staff_name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Date:</span>
            <span className="text-foreground">{schedule.date}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Time:</span>
            <span className="text-foreground">{schedule.start_time} - {schedule.end_time}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Hours:</span>
            <span className="text-foreground">{schedule.hours?.toFixed(1) || '-'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Status:</span>
            <span className="text-foreground capitalize">{schedule.status}</span>
          </div>
          {schedule.is_leave && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Leave Type:</span>
              <span className="text-amber-600 dark:text-amber-400 capitalize">{schedule.leave_type}</span>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose}>Close</Button>
          {schedule.status !== 'cancelled' && !schedule.is_leave && (
            <Button variant="destructive" onClick={onCancel}>Cancel Shift</Button>
          )}
        </div>
      </div>
    </div>
  );
}

// Generate Schedules Panel
function GenerateSchedulesPanel({ staff, templates, onGenerate }) {
  const [selectedStaff, setSelectedStaff] = useState([]);
  const [dateFrom, setDateFrom] = useState(() => {
    const today = new Date();
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7 - today.getDay()); // Next Monday
    return nextWeek.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => {
    const today = new Date();
    const nextWeekEnd = new Date(today);
    nextWeekEnd.setDate(today.getDate() + 13 - today.getDay()); // Next Sunday
    return nextWeekEnd.toISOString().split('T')[0];
  });
  const [generating, setGenerating] = useState(false);

  const toggleStaff = (staffId) => {
    setSelectedStaff(prev => 
      prev.includes(staffId) 
        ? prev.filter(id => id !== staffId)
        : [...prev, staffId]
    );
  };

  const selectAll = () => setSelectedStaff(staff.map(s => s.id));
  const selectNone = () => setSelectedStaff([]);

  const handleGenerate = async () => {
    if (selectedStaff.length === 0) {
      toast.error('Select at least one staff member');
      return;
    }
    setGenerating(true);
    await onGenerate(selectedStaff, dateFrom, dateTo);
    setGenerating(false);
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h3 className="font-medium text-foreground mb-2">Auto-Generate Schedules</h3>
        <p className="text-sm text-muted-foreground">
          Generate schedules based on staff patterns. Staff must have a pattern defined to be included.
        </p>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-foreground">Select Staff</label>
          <div className="flex gap-2">
            <button onClick={selectAll} className="text-xs text-primary hover:underline">Select All</button>
            <button onClick={selectNone} className="text-xs text-primary hover:underline">Select None</button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {staff.map(s => (
            <button
              key={s.id}
              onClick={() => toggleStaff(s.id)}
              className={`p-2 rounded-lg text-left text-sm transition-colors
                ${selectedStaff.includes(s.id) 
                  ? 'bg-primary text-white' 
                  : 'bg-muted text-foreground hover:bg-muted/80'}`}
            >
              <p className="font-medium truncate">{s.name}</p>
              <p className={`text-xs ${selectedStaff.includes(s.id) ? 'text-white/70' : 'text-muted-foreground'}`}>{s.role}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">From Date</label>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">To Date</label>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
      </div>

      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
          <div className="text-sm text-amber-800 dark:text-amber-300">
            <p className="font-medium">Before generating:</p>
            <ul className="list-disc list-inside mt-1 space-y-1 text-amber-700 dark:text-amber-400">
              <li>Ensure staff have schedule patterns defined</li>
              <li>Existing schedules for selected dates will not be overwritten</li>
              <li>You can override individual shifts after generation</li>
            </ul>
          </div>
        </div>
      </div>

      <Button 
        onClick={handleGenerate} 
        disabled={generating || selectedStaff.length === 0}
        className="w-full"
        data-testid="generate-schedules-btn"
      >
        {generating ? 'Generating...' : `Generate Schedules for ${selectedStaff.length} Staff`}
      </Button>
    </div>
  );
}
